import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { useAuth } from '../../lib/auth/AuthProvider';
import {
  assistiveTouchVisibleAtom,
  assistiveTouchPositionAtom,
  assistiveTouchDraggingAtom,
  assistiveTouchTempHiddenAtom,
  assistiveTouchMenuOpenAtom,
  showShakeUIAccessoryAtom,
  showHotjarSurveyAccessoryAtom
} from '../../atoms/assistiveTouchAtoms';
import { useVersionRouter } from '../../hooks/useVersionRouter';
import { useThemeMode } from '../../hooks/v0/useThemeMode';
import IOSThemeSelector from '../../pages/v3/components/ios/IOSThemeSelector';
import { useTranslation } from 'react-i18next';

// 小圆球尺寸
const BALL_SIZE = 44;
// 边缘吸附阈值
const EDGE_THRESHOLD = 20;

// Helper function to clamp position within window bounds
const clampPosition = (pos: { x: number; y: number }, windowSize: { width: number; height: number }) => {
  let newX = pos.x;
  let newY = pos.y;

  newX = Math.max(0, Math.min(newX, windowSize.width - BALL_SIZE));
  newY = Math.max(0, Math.min(newY, windowSize.height - BALL_SIZE));

  return { x: newX, y: newY };
};

export const AssistiveTouch: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const [position, setPosition] = useAtom(assistiveTouchPositionAtom);
  const [isDragging, setIsDragging] = useAtom(assistiveTouchDraggingAtom);
  const [isVisible, setIsVisible] = useAtom(assistiveTouchVisibleAtom);
  const [isTempHidden, setIsTempHidden] = useAtom(assistiveTouchTempHiddenAtom);
  const [isMenuOpen, setIsMenuOpen] = useAtom(assistiveTouchMenuOpenAtom);
  const { activeVersion, switchVersion } = useVersionRouter();
  const { toggleColorMode, isDarkMode, themeMode } = useThemeMode();
  const [showShakeUI, setShowShakeUI] = useAtom(showShakeUIAccessoryAtom);
  const [showHotjarSurvey, setShowHotjarSurvey] = useAtom(showHotjarSurveyAccessoryAtom);
  const [isThemeSelectorOpen, setIsThemeSelectorOpen] = useState(false);

  const ballRef = useRef<HTMLDivElement>(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Effect to validate and correct position loaded from storage
  useEffect(() => {
    let correctedX = position.x;
    let correctedY = position.y;
    let needsCorrection = false;

    if (position.x < 0) {
      correctedX = 0;
      needsCorrection = true;
    }
    if (position.y < 0) {
      correctedY = 0;
      needsCorrection = true;
    }

    if (needsCorrection) {
      setPosition({ x: correctedX, y: correctedY });
    }
    // Run only once on mount after initial position is loaded

  }, []);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      const newWindowSize = { width: newWidth, height: newHeight };

      setWindowSize(newWindowSize);

      // 确保小圆球不会超出屏幕边界
      // Clamp existing position based on new window size
      setPosition(prevPosition => clampPosition(prevPosition, newWindowSize));
    };

    // Initial check and clamp on mount
    // This ensures that the position from localStorage is validated against current window size on load
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);

  }, [setPosition]); // setPosition from useAtom is stable

  // 处理拖拽开始
  const handleDragStart = (e: React.PointerEvent) => {
    if (isMenuOpen) return;

    e.preventDefault();
    setIsDragging(true);

    // 记录起始位置
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });

    // 捕获指针
    if (ballRef.current) {
      ballRef.current.setPointerCapture(e.pointerId);
    }
  };

  // 处理拖拽移动
  const handleDragMove = (e: React.PointerEvent) => {
    if (!isDragging) return;

    e.preventDefault();

    // 计算新位置
    let newX = e.clientX - startPos.x;
    let newY = e.clientY - startPos.y;

    // 限制在屏幕内
    newX = Math.max(0, Math.min(newX, windowSize.width - BALL_SIZE));
    newY = Math.max(0, Math.min(newY, windowSize.height - BALL_SIZE));

    setPosition({ x: newX, y: newY });
  };

  // 处理拖拽结束
  const handleDragEnd = (e: React.PointerEvent) => {
    if (!isDragging) return;

    setIsDragging(false);

    // 释放指针捕获
    if (ballRef.current) {
      ballRef.current.releasePointerCapture(e.pointerId);
    }

    // 吸附到最近的边缘
    let snappedX = position.x;
    let snappedY = position.y;

    // 水平吸附
    if (position.x < EDGE_THRESHOLD) {
      snappedX = 0;
    } else if (position.x > windowSize.width - BALL_SIZE - EDGE_THRESHOLD) {
      snappedX = windowSize.width - BALL_SIZE;
    }

    // 垂直吸附
    if (position.y < EDGE_THRESHOLD) {
      snappedY = 0;
    } else if (position.y > windowSize.height - BALL_SIZE - EDGE_THRESHOLD) {
      snappedY = windowSize.height - BALL_SIZE;
    }

    // Final clamp after snapping logic
    const finalClampedPosition = clampPosition({ x: snappedX, y: snappedY }, windowSize);
    setPosition(finalClampedPosition);
  };

  // 处理点击
  const handleClick = () => {
    if (isDragging) return;
    setIsMenuOpen(!isMenuOpen);
  };

  // 切换版本并关闭菜单
  const handleVersionSwitch = (version: string) => {
    try {
      // 先关闭菜单，避免UI状态问题
      setIsMenuOpen(false);

      // 延迟一下再切换版本，确保菜单关闭动画完成
      setTimeout(() => {
        console.log(`[AssistiveTouch] Switching to version: ${version}`);
        switchVersion(version);
      }, 50);
    } catch (error) {
      console.error('[AssistiveTouch] Version switch failed:', error);
      // 可以在这里添加错误处理，例如显示一个toast提示
    }
  };

  // 处理主题选择器打开
  const handleOpenThemeSelector = () => {
    setIsThemeSelectorOpen(true);
    setIsMenuOpen(false); // 关闭主菜单
  };

  // Toggle Shake SDK UI visibility via body class
  const handleToggleShakeUIVisibility = () => {
    setShowShakeUI(!showShakeUI);
    setIsMenuOpen(false); // Close menu after action
  };

  // Toggle Hotjar Survey visibility via body class
  const handleToggleHotjarSurveyVisibility = () => {
    setShowHotjarSurvey(!showHotjarSurvey);
    setIsMenuOpen(false); // Close menu after action
  };

  // Effect to add/remove body class for Shake UI
  useEffect(() => {
    if (showShakeUI) {
      document.body.classList.add('tempo-show-shake-ui');
    } else {
      document.body.classList.remove('tempo-show-shake-ui');
    }
    // Cleanup function to remove class if component unmounts while UI is shown
    return () => document.body.classList.remove('tempo-show-shake-ui');
  }, [showShakeUI]);

  // Effect to add/remove body class for Hotjar Survey
  useEffect(() => {
    if (showHotjarSurvey) {
      document.body.classList.add('tempo-show-hotjar-survey');
    } else {
      document.body.classList.remove('tempo-show-hotjar-survey');
    }
    // Cleanup function to remove class if component unmounts while survey is shown
    return () => document.body.classList.remove('tempo-show-hotjar-survey');
  }, [showHotjarSurvey]);

  // 临时隐藏小圆球
  const hideTemporarily = () => {
    setIsTempHidden(true);
    setIsMenuOpen(false);

    // 30秒后自动显示
    setTimeout(() => {
      setIsTempHidden(false);
    }, 30000);
  };

  // 只有在临时隐藏时不显示，登录页面也可以显示
  if (isTempHidden) {
    return null;
  }

  // 使用 isDarkMode 状态而不是每次渲染都调用 getCurrentTheme()
  const currentThemeForButton = isDarkMode ? 'dark' : 'light';

  return (
    <>
      {/* 小圆球 */}
      <div
        ref={ballRef}
        className={`fixed z-[999999] rounded-full bg-white/40 dark:bg-gray-800/40 backdrop-blur-md flex items-center justify-center cursor-grab shadow-sm transition-opacity ${isDragging ? 'cursor-grabbing' : ''} ${isVisible ? 'opacity-70' : 'opacity-0 pointer-events-none'} hover:opacity-100`}
        style={{
          width: `${BALL_SIZE}px`,
          height: `${BALL_SIZE}px`,
          left: `${position.x}px`,
          top: `${position.y}px`,
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
        onClick={handleClick}
      >
        <span className="text-gray-400 dark:text-gray-500 text-xs font-medium select-none">{t('common:assistiveTouch.label')}</span>
      </div>

      {/* 菜单 */}
      {isMenuOpen && (
        <div
          className="fixed z-[999998] bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-xl p-3 border border-gray-200 dark:border-gray-600"
          style={{
            left: position.x < windowSize.width / 2 ? `${position.x + BALL_SIZE + 10}px` : `${position.x - 150 - 10}px`,
            top: `${position.y}px`,
            width: '150px',
          }}
        >
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-1">{t('common:assistiveTouch.switchVersion')}</h3>

            {/* 登录页面版本切换 */}
            {!user && (
              <>
                <button
                  onClick={() => window.location.href = '/v0/login'}
                  className={`text-left px-3 py-1.5 rounded-md text-sm text-gray-700 dark:text-gray-200 ${window.location.pathname === '/v0/login' ? 'bg-gray-200 dark:bg-gray-600 font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  {t('common:assistiveTouch.v0LoginPage')}
                </button>

                <button
                  onClick={() => window.location.href = '/v3/login'}
                  className={`text-left px-3 py-1.5 rounded-md text-sm text-gray-700 dark:text-gray-200 ${window.location.pathname === '/v3/login' ? 'bg-gray-200 dark:bg-gray-600 font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  {t('common:assistiveTouch.v3LoginPage')}
                </button>
              </>
            )}

            {/* 已登录用户的版本切换 */}
            {user && (
              <>
                <button
                  onClick={() => handleVersionSwitch('v0')}
                  className={`text-left px-3 py-1.5 rounded-md text-sm text-gray-700 dark:text-gray-200 ${activeVersion === 'v0' ? 'bg-gray-200 dark:bg-gray-600 font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  {t('common:assistiveTouch.v0Version')}
                </button>

                <button
                  onClick={() => handleVersionSwitch('v1')}
                  className={`text-left px-3 py-1.5 rounded-md text-sm text-gray-700 dark:text-gray-200 ${activeVersion === 'v1' ? 'bg-gray-200 dark:bg-gray-600 font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  {t('common:assistiveTouch.v1Version')}
                </button>

                <button
                  onClick={() => handleVersionSwitch('v3')}
                  className={`text-left px-3 py-1.5 rounded-md text-sm text-gray-700 dark:text-gray-200 ${activeVersion === 'v3' ? 'bg-gray-200 dark:bg-gray-600 font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  {t('common:assistiveTouch.v3Version')}
                </button>
              </>
            )}

            <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>

            <button
              onClick={handleOpenThemeSelector}
              className="text-left px-3 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {t('common:assistiveTouch.themeMode')}: {
                themeMode === 'light' ? t('common:assistiveTouch.themeLight') :
                themeMode === 'dark' ? t('common:assistiveTouch.themeDark') :
                t('common:assistiveTouch.themeSystem')
              }
            </button>

            {/* Button to toggle Shake SDK UI visibility */}
            <button
              onClick={handleToggleShakeUIVisibility}
              className="text-left px-3 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {showShakeUI ? t('common:assistiveTouch.hide') : t('common:assistiveTouch.show')} {t('common:assistiveTouch.shakeReporting')}
            </button>

            {/* Button to toggle Hotjar Survey visibility */}
            <button
              onClick={handleToggleHotjarSurveyVisibility}
              className="text-left px-3 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {showHotjarSurvey ? t('common:assistiveTouch.hide') : t('common:assistiveTouch.show')} {t('common:assistiveTouch.hotjarSurvey')}
            </button>

            <button
              onClick={hideTemporarily}
              className="text-left px-3 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {t('common:assistiveTouch.hideTemporarily')}
            </button>
          </div>
        </div>
      )}

      {/* 点击外部关闭菜单 */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-[9997]"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* 主题选择器 */}
      <IOSThemeSelector
        isOpen={isThemeSelectorOpen}
        onClose={() => setIsThemeSelectorOpen(false)}
      />
    </>
  );
};

export default AssistiveTouch;
